import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/i18n";
import { PaywallProvider, usePaywall } from "@/contexts/PaywallContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import AppLayout from "@/components/layout/AppLayout";
import PaywallModal from "@/components/PaywallModal";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Reperes from "@/pages/Reperes";
import PrisesDeContact from "@/pages/PrisesDeContact";
import MandatsSignes from "@/pages/MandatsSignes";
import Clubs from "@/pages/Clubs";
import TeamProfile from "@/pages/TeamProfile";
import CalendarPage from "@/pages/Calendar";
import ImportPage from "@/pages/Import";
import Settings from "@/pages/Settings";
import TitleManager from "@/components/TitleManager";
import Relances from "@/pages/Relances";
import PlayerProfile from "@/pages/PlayerProfile";
import NegotiationProfile from "@/pages/NegotiationProfile";
import NegotiationWorkspace from "@/pages/NegotiationWorkspace";
import CareerPlanPage from "@/pages/CareerPlan";
import CareerPlanBuilder from "@/pages/CareerPlanBuilder";
import Documents from "@/pages/Documents";
import Billing from "@/pages/Billing";
import BillingPlans from "@/pages/BillingPlans";
import BillingSuccess from "@/pages/BillingSuccess";
import Performance from "@/pages/Performance";
import Intelligence from "@/pages/Intelligence";
import Contracts from "@/pages/Contracts";
import Placements from "@/pages/Placements";
import DiscussionsActives from "@/pages/DiscussionsActives";
import ContractWorkspace from "@/pages/ContractWorkspace";
import RenewalWorkspace from "@/pages/RenewalWorkspace";
import ContractPerformance from "@/pages/ContractPerformance";
import JoinWorkspace from "@/pages/JoinWorkspace";
import AdminConsole from "@/pages/AdminConsole";
import { Toaster } from "sonner";

function PaywallEventBridge() {
  const { show } = usePaywall();
  React.useEffect(() => {
    const handler = (e) => show(e.detail?.message);
    window.addEventListener("athlynx:paywall", handler);
    return () => window.removeEventListener("athlynx:paywall", handler);
  }, [show]);
  return null;
}

function AppShell({ children }) {
  return <AppLayout>{children}</AppLayout>;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of session_id in URL fragment to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/join" element={<JoinWorkspace />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={<ProtectedRoute><AppShell><Dashboard /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/pipeline"
        element={<ProtectedRoute><AppShell><Pipeline /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/reperes"
        element={<ProtectedRoute><AppShell><Reperes /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/prises-de-contact"
        element={<ProtectedRoute><AppShell><PrisesDeContact /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/mandats-signes"
        element={<ProtectedRoute><AppShell><MandatsSignes /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/placements"
        element={<ProtectedRoute><AppShell><Placements /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/placements/:placementId"
        element={<ProtectedRoute><AppShell><Placements /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/discussions-actives"
        element={<ProtectedRoute><AppShell><DiscussionsActives /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/discussions-actives/:discussionId"
        element={<ProtectedRoute><AppShell><DiscussionsActives /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/clubs"
        element={<ProtectedRoute><AppShell><Clubs /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/clubs/:cid"
        element={<ProtectedRoute><AppShell><TeamProfile /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/calendar"
        element={<ProtectedRoute><AppShell><CalendarPage /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/import"
        element={<ProtectedRoute><AppShell><ImportPage /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/relances"
        element={<ProtectedRoute><AppShell><Relances /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/players/:id"
        element={<ProtectedRoute><AppShell><PlayerProfile /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/players/:id/career-plan"
        element={<ProtectedRoute><AppShell><CareerPlanPage /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/players/:id/career-plan/builder"
        element={<ProtectedRoute><AppShell><CareerPlanBuilder /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/players/:id/negotiation-profile"
        element={<ProtectedRoute><AppShell><NegotiationWorkspace /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/players/:id/negotiation-profile/:step"
        element={<ProtectedRoute><AppShell><NegotiationProfile /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/players/:id/negotiation-profiles"
        element={<ProtectedRoute><AppShell><NegotiationProfile listView /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/documents"
        element={<ProtectedRoute><AppShell><Documents /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/discussions"
        element={<Navigate to="/pipeline?status=En%20discussion" replace />}
      />
      <Route
        path="/settings"
        element={<ProtectedRoute><AppShell><Settings /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/settings/:section"
        element={<ProtectedRoute><AppShell><Settings /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/billing"
        element={<ProtectedRoute><AppShell><Billing /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/performance"
        element={<ProtectedRoute><AppShell><Performance /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/intelligence"
        element={<ProtectedRoute><AppShell><Intelligence /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/performance/contracts"
        element={<ProtectedRoute><AppShell><ContractPerformance /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/contracts"
        element={<ProtectedRoute><AppShell><Contracts /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/contracts/renewals/:rid"
        element={<ProtectedRoute><AppShell><RenewalWorkspace /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/contracts/:id"
        element={<ProtectedRoute><AppShell><ContractWorkspace /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/billing/plans"
        element={<ProtectedRoute><AppShell><BillingPlans /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/billing/success"
        element={<ProtectedRoute><AppShell><BillingSuccess /></AppShell></ProtectedRoute>}
      />
      <Route
        path="/pricing"
        element={<Navigate to="/billing/plans" replace />}
      />
      <Route
        path="/offre"
        element={<Navigate to="/pricing" replace />}
      />
      <Route
        path="/admin"
        element={<ProtectedRoute><AppShell><AdminConsole /></AppShell></ProtectedRoute>}
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <TitleManager />
          <AuthProvider>
            <LanguageProvider>
            <PaywallProvider>
              <PaywallEventBridge />
              <AppRouter />
              <PaywallModal />
              <Toaster theme="system" richColors closeButton />
            </PaywallProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
